import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { Reveal } from './MobileSections';

const A = 'https://customer-assets.emergentagent.com/job_glass-quote-hub-1/artifacts/';
export const GALLERY = [
  { src: `${A}1cc3qzta_Van%20with%20Parliment.png`, alt: 'A+ Auto Glass mobile service van at Parliament Hill', label: 'Mobile Service Van', pos: '60% 45%' },
  { src: `${A}h8jhhrei_image.png`, alt: 'Technicians installing a new windshield on an SUV', label: 'Windshield Installation', pos: 'center' },
  { src: `${A}by2b2uho_adas_service_image.png`, alt: 'ADAS camera calibration', label: 'ADAS Calibration', pos: 'center' },
  { src: `${A}ux9fi96v_image.png`, alt: 'The A+ Auto Glass team', label: 'Our Team', pos: '70% center' },
  { src: `${A}7nxdtj5q_windshield_service_image.png`, alt: 'Windshield replacement glass', label: 'OEM-Quality Glass', pos: 'center' },
  { src: `${A}9qkld0v6_image.png`, alt: 'A+ Auto Glass front desk', label: 'Our Orléans Shop', pos: '30% center' },
  { src: `${A}apghmpc1_mobile_service_image.png`, alt: 'Mobile windshield service', label: 'We Come To You', pos: 'center' },
];

export function useSnapIndex(ref, count) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const child = el.firstElementChild;
      if (!child) return;
      const step = child.offsetWidth + parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap || 0);
      setIndex(Math.min(count - 1, Math.max(0, Math.round(el.scrollLeft / step))));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [ref, count]);
  return index;
}

export function Dots({ count, index, dark }) {
  return (
    <div className="flex justify-center gap-1.5" data-testid="carousel-dots">
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? 'w-5 bg-[#0D5BD7]' : `w-1.5 ${dark ? 'bg-white/30' : 'bg-slate-300'}`}`} />
      ))}
    </div>
  );
}

export default function MobileGallery() {
  const ref = useRef(null);
  const index = useSnapIndex(ref, GALLERY.length);
  const [all, setAll] = useState(false);

  return (
    <section data-testid="mobile-gallery" className="bg-[#F4F6FA] py-14">
      <Reveal className="px-5 flex items-end justify-between">
        <h2 className="text-[30px] leading-[1.05] font-black tracking-[-0.02em] text-[#111827]">Our Work Speaks<br />for Itself</h2>
        <button onClick={() => setAll(true)} data-testid="gallery-view-all-btn" className="text-[13px] font-bold text-[#0D5BD7] flex items-center gap-1 pb-1 shrink-0">View All <ArrowRight className="w-4 h-4" /></button>
      </Reveal>

      <div ref={ref} className="mt-6 flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar px-5 scroll-px-5" data-testid="gallery-scroller">
        {GALLERY.map((g) => (
          <figure key={g.src} className="snap-start shrink-0 w-[78vw] max-w-[340px]">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-slate-200 shadow-[0_10px_30px_rgba(15,23,42,0.1)]">
              <img src={g.src} alt={g.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: g.pos }} />
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent" />
              <figcaption className="absolute left-4 bottom-3.5 text-white text-[13px] font-bold tracking-wide">{g.label}</figcaption>
            </div>
          </figure>
        ))}
      </div>
      <div className="mt-5"><Dots count={GALLERY.length} index={index} /></div>

      <AnimatePresence>
        {all && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] bg-[#0a1220]/95 backdrop-blur-xl overflow-y-auto" data-testid="gallery-overlay" onClick={() => setAll(false)}>
            <div className="sticky top-0 flex items-center justify-between px-5 h-16 text-white">
              <span className="text-[12px] font-bold tracking-[0.3em]">OUR WORK</span>
              <button aria-label="Close" data-testid="gallery-close-btn" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-4 pb-24 grid grid-cols-2 gap-3" onClick={(e) => e.stopPropagation()}>
              {GALLERY.map((g, i) => (
                <motion.div key={g.src} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className={`relative rounded-2xl overflow-hidden ${i === 0 ? 'col-span-2 aspect-[16/10]' : 'aspect-square'}`}>
                  <img src={g.src} alt={g.alt} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: g.pos }} />
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute left-3 bottom-2.5 text-white text-[12px] font-bold">{g.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
